import { ref, computed, readonly } from 'vue'
import type { Member } from '@/data/seedMember'
import { BLANK_MEMBER } from '@/data/seedMember'
import {
  login as loginRequest,
  verifyLoginCode as verifyLoginCodeRequest,
  resendLoginCode as resendLoginCodeRequest,
} from '@/services/auth'
import { getMe, listContracts } from '@/services/account'
import { mapUserProfile } from '@/mappers/member.mapper'
import type { RawContract, RawDocument } from '@/types/api'
import { getAuthToken, setAuthToken, clearAuthToken, onAuthTokenChange } from '@/services/authSession'
import { ApiError } from '@/services/http'

/**
 * État d'authentification de l'espace personnel.
 *
 * La source de vérité est le token JWT de session (`authSession`), persisté en
 * `localStorage` (partagé et synchronisé entre onglets). `isAuthenticated` dérive
 * d'une copie réactive de ce token. Le profil de l'adhérent (`GET /me`) alimente
 * `member`, les contrats (`GET /contracts`) `rawContracts` et les documents
 * (`/me`) `rawDocuments` ; tous mappés en view-models par les composables dédiés.
 *
 * `BLANK_MEMBER` sert de placeholder neutre tant que `GET /me` n'a pas répondu
 * (évite un flash « non connecté » au rechargement). Voir [[src/data/seedMember.ts]].
 *
 * Quand la double authentification est activée côté back, la connexion se fait
 * en deux temps : `login()` ne rend pas de session mais ouvre un « challenge »
 * (code envoyé par email), que `verifyLoginCode()` conclut. Seule cette seconde
 * étape ouvre alors la session.
 */

/** Issue d'une tentative de connexion, mappée depuis l'API par le composable
 *  pour que la vue ne manipule que des messages localisés. `mfa_required` signale
 *  que le mot de passe est bon mais qu'un code reçu par email reste à saisir. */
export type LoginResult =
  | 'success'
  | 'mfa_required'
  | 'invalid_credentials'
  | 'account_blocked'
  | 'rate_limited'
  | 'network_error'

/** Issue de la saisie du code de double authentification. `challenge_expired`
 *  signale qu'il n'y a plus de connexion en cours : il faut repartir du mot de passe. */
export type VerifyCodeResult =
  | 'success'
  | 'invalid_code'
  | 'challenge_expired'
  | 'account_blocked'
  | 'rate_limited'
  | 'network_error'

/** Issue d'une demande de nouveau code. */
export type ResendCodeResult =
  | 'sent'
  | 'challenge_expired'
  | 'account_blocked'
  | 'rate_limited'
  | 'network_error'

/** Vocabulaire commun des échecs des trois appels de connexion, avant que chacun
 *  ne renomme `unauthorized` dans ses propres termes. */
type AuthFailure = 'unauthorized' | 'account_blocked' | 'rate_limited' | 'network_error'

/** Traduit un échec HTTP en cause métier. Statut 0 (réseau/CORS/abort) et toute
 *  défaillance inattendue retombent sur `network_error`. */
function authFailure(err: unknown): AuthFailure {
  if (err instanceof ApiError) {
    if (err.status === 401) return 'unauthorized'
    if (err.status === 403) return 'account_blocked'
    if (err.status === 429) return 'rate_limited'
  }
  return 'network_error'
}

// État partagé (singleton module-level) : une seule session connectée à la fois.
// `getAuthToken()` est SSR-safe (renvoie null pendant le prerender).
const token = ref<string | null>(getAuthToken())
// À l'init, member part d'un placeholder neutre dès qu'un token existe (évite un
// flash « non connecté » au F5) ; loadMember() le remplace par le profil réel.
const member = ref<Member | null>(token.value ? BLANK_MEMBER : null)
// Vrai dès que `GET /me` a répondu au moins une fois (member contient le profil
// réel, plus le placeholder). Sert à attendre la donnée avant de pré-remplir un
// formulaire (ex. édition ouverte via deep-link).
const memberLoaded = ref(false)
// Contrats bruts de l'API (mappés en view-model par useContracts, locale-réactif).
const rawContracts = ref<RawContract[]>([])
const contractsLoading = ref(false)
// Documents bruts de /me (mappés en view-model par useDocuments).
const rawDocuments = ref<RawDocument[]>([])
/** Connexion en cours entre les deux étapes de la double authentification.
 *  L'email accompagne le jeton pour que l'écran de saisie du code puisse le
 *  rappeler — y compris quand la connexion a été lancée depuis un autre écran
 *  (carte de connexion de l'accueil) que celui qui saisit le code. */
type PendingChallenge = { token: string; email: string }

// Volontairement en mémoire seule : le jeton ne vit que 10 minutes et n'ouvre
// aucune session — un rechargement de page refait simplement le login.
const challenge = ref<PendingChallenge | null>(null)

/**
 * Charge le profil de l'adhérent depuis `GET /me` (+ ses documents) et le pose
 * dans `member`. Silencieux en cas d'échec : un 401 terminal est géré par le
 * transport (setUnauthorizedHandler) ; sinon on conserve l'état courant.
 */
async function loadMember(): Promise<void> {
  try {
    const user = await getMe()
    const base = member.value ?? BLANK_MEMBER
    member.value = { ...base, ...mapUserProfile(user) }
    rawDocuments.value = user.documents ?? []
    memberLoaded.value = true
  } catch {
    /* on garde le placeholder / profil courant */
  }
}

/** Charge les contrats de l'adhérent depuis `GET /contracts`. Silencieux en cas
 *  d'échec (401 géré par le transport ; sinon liste vide). */
async function loadContracts(): Promise<void> {
  contractsLoading.value = true
  try {
    rawContracts.value = await listContracts()
  } catch {
    /* liste vide en cas d'échec non-401 */
  } finally {
    contractsLoading.value = false
  }
}

// Au démarrage : si une session existe déjà, rafraîchir profil + contrats.
if (token.value) {
  void loadMember()
  void loadContracts()
}

// Synchro inter-onglets : une connexion/déconnexion dans un autre onglet de la
// même origine se répercute ici (le token est partagé via localStorage).
onAuthTokenChange((next) => {
  token.value = next
  member.value = next ? member.value ?? BLANK_MEMBER : null
  if (next) {
    void loadMember()
    void loadContracts()
  } else {
    rawContracts.value = []
    rawDocuments.value = []
    memberLoaded.value = false
  }
})

export function useAuth() {
  const isAuthenticated = computed(() => token.value !== null)

  // UUID backend de l'utilisateur connecté, ou null tant que `GET /me` n'a pas
  // renseigné le profil réel (le placeholder BLANK_MEMBER a un id vide). Sert à
  // construire un contract_draft de souscripteur connecté (user_id + user_role).
  const userId = computed(() => member.value?.id || null)

  /** Une connexion attend la saisie du code reçu par email. */
  const isAwaitingCode = computed(() => challenge.value !== null)
  /** Email de cette connexion en cours, pour l'écran de saisie du code. */
  const pendingLoginEmail = computed(() => challenge.value?.email ?? null)

  /**
   * Ouvre la session : persiste le token, pose un profil provisoire, puis charge
   * le profil réel (attendu, pour que la redirection ait la donnée) et les
   * contrats (en arrière-plan). Seul chemin d'entrée en session, que la double
   * authentification soit active ou non.
   */
  async function openSession(authToken: string): Promise<void> {
    challenge.value = null // la connexion en cours est terminée
    token.value = authToken
    setAuthToken(authToken)
    member.value = BLANK_MEMBER // placeholder immédiat (évite un flash null avant /me)
    await loadMember() // enrichit avec le profil réel avant la redirection
    void loadContracts() // contrats en arrière-plan
  }

  /**
   * Étape 1 : vérifie les identifiants. Ouvre la session directement quand la
   * double authentification est désactivée côté back, sinon mémorise la connexion
   * en cours et rend `mfa_required` — la vue enchaîne alors sur la saisie du code.
   */
  async function login(email: string, password: string): Promise<LoginResult> {
    try {
      const outcome = await loginRequest(email, password)
      if (outcome.kind === 'mfa_required') {
        challenge.value = { token: outcome.mfaToken, email }
        return 'mfa_required'
      }
      await openSession(outcome.authToken)
      return 'success'
    } catch (err) {
      const failure = authFailure(err)
      return failure === 'unauthorized' ? 'invalid_credentials' : failure
    }
  }

  /**
   * Étape 2 : conclut la connexion en cours avec le code reçu par email.
   * Le back ne distingue pas un code refusé d'un jeton de connexion périmé : les
   * deux remontent en 401, donc en `invalid_code` — l'utilisateur s'en sort en
   * demandant un nouveau code, ce qui, lui, révèle une connexion expirée.
   */
  async function verifyLoginCode(code: string): Promise<VerifyCodeResult> {
    if (!challenge.value) return 'challenge_expired'
    try {
      const authToken = await verifyLoginCodeRequest(challenge.value.token, code)
      await openSession(authToken)
      return 'success'
    } catch (err) {
      const failure = authFailure(err)
      return failure === 'unauthorized' ? 'invalid_code' : failure
    }
  }

  /** Redemande un code pour la connexion en cours. Le jeton rendu remplace le
   *  précédent ; un 401 signifie que la connexion en cours a expiré. */
  async function resendLoginCode(): Promise<ResendCodeResult> {
    if (!challenge.value) return 'challenge_expired'
    try {
      const token = await resendLoginCodeRequest(challenge.value.token)
      challenge.value = { ...challenge.value, token }
      return 'sent'
    } catch (err) {
      const failure = authFailure(err)
      if (failure === 'unauthorized') {
        challenge.value = null // plus de connexion en cours : retour au mot de passe
        return 'challenge_expired'
      }
      return failure
    }
  }

  /** Abandonne la connexion en cours (retour à l'étape mot de passe). */
  function cancelLogin(): void {
    challenge.value = null
  }

  /** Termine la session (côté client uniquement — pas d'endpoint de logout). */
  function logout(): void {
    token.value = null
    challenge.value = null
    clearAuthToken()
    member.value = null
    rawContracts.value = []
    rawDocuments.value = []
    memberLoaded.value = false
  }

  return {
    member: readonly(member),
    isAuthenticated,
    userId,
    memberLoaded: readonly(memberLoaded),
    rawContracts,
    rawDocuments,
    contractsLoading: readonly(contractsLoading),
    reloadMember: loadMember,
    reloadContracts: loadContracts,
    isAwaitingCode,
    pendingLoginEmail,
    login,
    verifyLoginCode,
    resendLoginCode,
    cancelLogin,
    logout,
  }
}
